import { z } from 'zod'
import { briefingModelSchema } from './ai-models'
import { DEFAULT_BRIEFING_INSTRUCTIONS } from './briefing-prompt'

/**
 * In-app settings — what an admin changes under Einstellungen rather than
 * somebody with access to the server changing an environment variable.
 *
 * THE LINE BETWEEN THE TWO IS DELIBERATE. A setting belongs here only if it is
 * a rule in the AGENCY's own words that the agency may want to change without a
 * redeploy. It stays in the environment when it is
 *
 *   - a secret, a path, a port or a URL — deploy-time by nature;
 *   - security policy (session lifetimes, the login limiter, the onOffice write
 *     guard): a hijacked admin session must not be able to loosen those;
 *   - something the agency must not SEE — the AI markup and the image model
 *     are absent from every response on purpose, and a settings screen would
 *     hand both over. (The monthly LIMIT is the agency's, in billed dollars,
 *     and is a setting; the margin under it is not.)
 *
 * The defaults live HERE, not in env.ts: the server falls back to them for a
 * key nobody has saved, and the page offers them as "Standard wiederherstellen",
 * so the two cannot disagree about what the default is.
 */

/**
 * A note that begins with one of these closes a checklist step as "nicht
 * zutreffend". Stored lowercase; matched as a case-insensitive PREFIX, so the
 * agency's habitual trailing "!" and any appended detail do not matter.
 */
const notePrefixSchema = z
  .string()
  .trim()
  .min(2, 'Mindestens zwei Zeichen — ein einzelner Buchstabe träfe fast jede Notiz')
  .max(60)
  .transform((value) => value.toLowerCase())

/*
 * Every GROUP is strict, not just the PATCH body around them. `.partial()` does
 * not push strictness down a level, so with plain objects here a typo INSIDE a
 * group — `notAplicableNotes` beside the real key — was stripped and the save
 * answered 200 having done nothing. That is the silent no-op the outer
 * `.strict()` exists to prevent, one level further in.
 */
/** A wall-clock time of day, "HH:MM", in the agency's timezone. */
export const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * One Makler as printed on a handover protocol. `id` is the client's own
 * (`crypto.randomUUID()`), only there so the editor can key a row; nothing
 * outside the list refers to it — a protocol COPIES the values in.
 */
export const handoverAgentSchema = z.strictObject({
  id: z.string().min(1).max(40),
  name: z.string().trim().min(1, 'Name fehlt').max(120),
  /** One line on the PDF, in the Kontakt cell of the Beteiligte table. */
  address: z.string().trim().max(300),
  phone: z.string().trim().max(60),
  email: z
    .string()
    .trim()
    .max(200)
    .refine((value) => value === '' || z.email().safeParse(value).success, {
      message: 'Keine gültige E-Mail-Adresse',
    }),
  isDefault: z.boolean(),
})

export type HandoverAgent = z.infer<typeof handoverAgentSchema>

/** The default entry: the one flagged, else the first. Null for an empty list. */
export function defaultHandoverAgent(agents: readonly HandoverAgent[]): HandoverAgent | null {
  return agents.find((agent) => agent.isDefault) ?? agents[0] ?? null
}

export const appSettingsSchema = z.object({
  checklist: z.strictObject({
    /**
     * May be empty: "no note ever closes a step" is a legitimate rule. Deduped
     * after lowercasing, so "Nicht vorhanden" and "nicht vorhanden" are one.
     */
    notApplicableNotes: z
      .array(notePrefixSchema)
      .max(20)
      .transform((values) => Array.from(new Set(values))),
  }),
  estates: z.strictObject({
    /**
     * Whether the sync mirrors archived objects too. Read when a sync STARTS —
     * changing it alters nothing until the next one runs.
     */
    includeArchived: z.boolean(),
  }),
  sync: z.strictObject({
    /**
     * Whether the checklist mirror refreshes itself. The agency's rule — how
     * fresh the Unterlagen matrix has to be is theirs to decide — but the
     * MECHANISM is the server's: the floor on how often a run may start and
     * the retry after a failure are constants in the API, not settings, because
     * every run spends onOffice requests.
     */
    enabled: z.boolean(),
    /**
     * When the daily run starts, as a time of day in REPORTING_TIMEZONE (the
     * agency's clock). A run that was missed — the container was down, or the
     * setting was switched on in the afternoon — is caught up at the next tick
     * rather than waiting for tomorrow.
     */
    dailyAt: z.string().regex(TIME_OF_DAY_PATTERN, 'Uhrzeit als HH:MM'),
  }),
  handovers: z.strictObject({
    /**
     * The agency's Makler, one of whom is seeded onto the Makler party of every
     * NEW protocol, and any of whom can be picked in the editor. A seed like
     * every other prefill: it stays editable in the protocol, and it never
     * rewrites one that already exists — a completed protocol is a signed
     * original, and a draft may have been corrected by hand.
     *
     * Which one is seeded (`resolveAgentDefaults` in the API): the entry whose
     * name IS the object's Betreuer in onOffice, else the default. An EMPTY list
     * means "use the object's Betreuer from onOffice", which is what happened
     * before this existed — and is the default, so it changes nothing.
     *
     * Exactly one default when the list is not empty: at most one may be
     * flagged, and with none flagged the FIRST counts (`defaultHandoverAgent`).
     */
    agents: z
      .array(handoverAgentSchema)
      .max(20)
      .refine((agents) => agents.filter((agent) => agent.isDefault).length <= 1, {
        message: 'Nur ein Makler kann der Standard sein',
      })
      .refine((agents) => new Set(agents.map((agent) => agent.id)).size === agents.length, {
        message: 'Doppelte Makler-Kennung',
      }),
  }),
  briefings: z.strictObject({
    /**
     * The model a new briefing runs on: provider and id as ONE value (see
     * `briefingModelSchema`). Direct Anthropic is an enum over the price table
     * — a model without a price would leave billing and the limit blind;
     * OpenRouter is any id in its format, because OpenRouter reports what each
     * call cost. Visible to admins by design — nothing in the choice reveals
     * the margin.
     */
    model: briefingModelSchema,
    /**
     * The agency's content instruction — what to look for and how to word it.
     * The section STRUCTURE is code (`briefingBodySchema`) and the hard rules
     * are the prompt's fixed frame, which this text refines and cannot lift.
     * The minimum stops a stray save of an empty box from leaving the model
     * with the frame alone.
     */
    instructions: z.string().trim().min(50).max(8000),
    /** Used when the agent leaves Sonderfragen empty. "" = none. */
    defaultSpecialQuestions: z.string().trim().max(2000),
  }),
  aiUsage: z.strictObject({
    /**
     * The most the AI features may bill in one calendar month, in BILLED
     * micro-dollars — the figure the agency sees, markup included — cut in
     * REPORTING_TIMEZONE. Null = no limit.
     *
     * The agency's rule in its own words, so it is a setting. It is NOT the
     * operator's protection: an admin can raise it. That is the spend limit at
     * each supplier, which lives outside the app and cannot be lifted from it.
     *
     * Checked when a job is CREATED, against an estimate. Running jobs finish,
     * so the last one of a month can overshoot by its own estimate error.
     */
    monthlyLimitMicroUsd: z.number().int().positive().nullable(),
  }),
})

export type AppSettings = z.infer<typeof appSettingsSchema>

export const DEFAULT_APP_SETTINGS: AppSettings = {
  checklist: { notApplicableNotes: ['nicht zutreffend', 'nicht vorhanden'] },
  estates: { includeArchived: false },
  sync: { enabled: true, dailyAt: '06:00' },
  handovers: { agents: [] },
  briefings: {
    model: { provider: 'anthropic', id: 'claude-opus-5-5' },
    instructions: DEFAULT_BRIEFING_INSTRUCTIONS,
    defaultSpecialQuestions: '',
  },
  aiUsage: { monthlyLimitMicroUsd: null },
}

/**
 * PATCH body. Each GROUP is optional, but a group that is sent is sent whole —
 * two levels of partial would buy nothing with one field per group, and would
 * make "was this key omitted or cleared?" a question the server has to answer.
 * `.strict()` so a typo'd key is a 400 rather than a silent no-op.
 */
export const updateAppSettingsSchema = appSettingsSchema.partial().strict()

export type UpdateAppSettings = z.infer<typeof updateAppSettingsSchema>

export const appSettingsResponseSchema = z.object({
  settings: appSettingsSchema,
  defaults: appSettingsSchema,
  /** Last time anything was saved. Null while every value is still a default. */
  updatedAt: z.iso.datetime().nullable(),
})

export type AppSettingsResponse = z.infer<typeof appSettingsResponseSchema>

/**
 * What a save answers with. `reevaluatedItems` is how many checklist cells
 * changed state because the note rule changed — applied to the local mirror in
 * the same transaction as the save, with no onOffice call. Zero when the rule
 * was not part of the change, or when it changed and no cell was affected.
 */
export const updateAppSettingsResponseSchema = appSettingsResponseSchema.extend({
  reevaluatedItems: z.number().int().nonnegative(),
})

export type UpdateAppSettingsResponse = z.infer<typeof updateAppSettingsResponseSchema>

/**
 * `GET /handovers/agents` — the Makler list for the protocol editor. Its own
 * route because the settings routes are admin-only as a whole and every agent
 * uses the editor. Nothing secret: the agency's own staff, whose details print
 * on every protocol anyway.
 */
export const handoverAgentsResponseSchema = z.object({
  agents: z.array(handoverAgentSchema),
})

export type HandoverAgentsResponse = z.infer<typeof handoverAgentsResponseSchema>
